import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Search, UserPlus, Briefcase, ArrowRight, Store } from "lucide-react";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { CATEGORIES } from "../lib/options";
import { Photo } from "../components/Chrome";
import { JobCard } from "../components/JobCard";
import { Empty, Spinner } from "../components/Bits";
import { useMarks } from "./Jobs";

function Hero() {
  const { t } = useT();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const go = (e) => { e.preventDefault(); nav(`/jobs${q ? `?q=${encodeURIComponent(q)}` : ""}`); };
  return (
    <section className="page grid items-center gap-8 pb-10 pt-6 md:grid-cols-2 md:pt-12">
      <div className="fade-up">
        <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight text-[color:var(--navy)] sm:text-5xl lg:text-6xl" data-testid="hero-title">{t("hero_title")}</h1>
        <p className="mt-4 text-base text-slate-600 sm:text-lg">{t("hero_sub")}</p>
        <form onSubmit={go} className="mt-6 flex gap-2" role="search">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={22} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_ph")} className="field !pl-12" data-testid="hero-search-input" aria-label={t("search")} />
          </div>
          <button className="grid min-h-[56px] w-14 place-items-center rounded-xl bg-[color:var(--navy)] text-white sm:w-auto sm:px-5 sm:font-bold" data-testid="hero-search-btn" aria-label={t("search")}>
            <Search size={22} className="sm:hidden" /><span className="hidden sm:inline">{t("search")}</span>
          </button>
        </form>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Link to="/jobs" className="btn-orange !w-full md:!px-4 md:!text-base" data-testid="btn-find-job"><Briefcase size={22} />{t("find_job")}</Link>
          <Link to="/start" className="btn-navy !w-full md:!px-4 md:!text-base" data-testid="btn-create-profile"><UserPlus size={22} />{t("create_profile")}</Link>
        </div>
        <SignupCounter />
      </div>
      <div className="relative fade-up" style={{ animationDelay: ".1s" }}>
        <div className="absolute -right-3 -top-3 hidden h-full w-full rounded-[2rem] bg-[color:var(--orange)] md:block" />
        <Photo k="team" eager className="relative h-48 w-full rounded-[2rem] object-[70%_center] sm:h-72 md:h-[440px]" />
      </div>
    </section>
  );
}

function SignupCounter() {
  const { t } = useT();
  const [n, setN] = useState(null);
  useEffect(() => { api.get("/public/stats").then((r) => setN(r.data.signups_24h)).catch(() => {}); }, []);
  if (n === null) return null;
  return (
    <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800" data-testid="signup-counter">
      <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" /></span>
      <b data-testid="signup-counter-value">{n}</b> {t("signups_24h")}
    </p>
  );
}

function LatestJobs() {
  const { t } = useT();
  const [jobs, setJobs] = useState(null);
  const { marks, toggleSave } = useMarks();
  useEffect(() => { api.get("/jobs", { params: { limit: 6 } }).then((r) => setJobs(r.data)).catch(() => setJobs([])); }, []);
  return (
    <section className="page py-12" data-testid="latest-jobs">
      <div className="flex items-end justify-between gap-3">
        <h2 className="text-2xl font-bold text-[color:var(--navy)] sm:text-3xl">{t("latest_jobs")}</h2>
        <Link to="/jobs" className="inline-flex min-h-[44px] items-center gap-1 font-bold text-[color:var(--orange-dark)]" data-testid="latest-jobs-see-all">{t("see_all")}<ArrowRight size={18} /></Link>
      </div>
      <div className="mt-6">
        {jobs === null ? <Spinner /> : jobs.length === 0 ? (
          <Empty icon={Briefcase} title={t("no_jobs_t")} sub={t("no_jobs_sub")} testid="latest-jobs-empty" />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{jobs.map((j) => <JobCard key={j.id} job={j} saved={marks.saved.includes(j.id)} onToggleSave={toggleSave} />)}</div>
        )}
      </div>
    </section>
  );
}

function Notice() {
  const { lang } = useT();
  const [n, setN] = useState("");
  useEffect(() => { api.get("/public/config").then((r) => setN(r.data.content?.[`notice_${lang}`] || "")).catch(() => {}); }, [lang]);
  if (!n) return null;
  return <div className="page"><p className="mt-4 rounded-2xl bg-[color:var(--orange-soft)] p-4 font-semibold text-[color:var(--navy)]" data-testid="home-notice">{n}</p></div>;
}

export default function Home() {
  const { t } = useT();
  const { user } = useAuth();
  const steps = [["how1", "how1d"], ["how2", "how2d"], ["how3", "how3d"]];
  return (
    <div>
      <Notice />
      <Hero />
      <LatestJobs />
      <section className="bg-slate-50 py-12">
        <div className="page grid items-center gap-6 md:grid-cols-[1fr_1.1fr]">
          <div>
          <h2 className="text-2xl font-bold text-[color:var(--navy)] sm:text-3xl">{t("cats_title")}</h2>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {CATEGORIES.map(({ v, icon: Icon }) => (
              <Link key={v} to={`/jobs?category=${v}`} data-testid={`home-cat-${v}`}
                className="card-soft flex min-h-[96px] flex-col items-start justify-between gap-3 p-4 transition-colors hover:border-[color:var(--orange)]">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-[color:var(--orange-soft)] text-[color:var(--orange)]"><Icon size={22} /></span>
                <span className="font-bold leading-tight text-slate-900">{t(`cat_${v}`)}</span>
              </Link>
            ))}
          </div>
          </div>
          <Photo k="shelves" className="order-first h-44 w-full rounded-[2rem] sm:h-64 md:order-none md:h-[420px]" />
        </div>
      </section>
      <section className="page grid items-center gap-8 py-14 md:grid-cols-2" data-testid="how-it-works">
        <div>
        <h2 className="text-2xl font-bold text-[color:var(--navy)] sm:text-3xl">{t("how_title")}</h2>
        <ol className="mt-8 grid gap-4">
          {steps.map(([a, b], i) => (
            <li key={a} className="card-soft flex gap-4 p-5">
              <span className="font-display grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[color:var(--navy)] text-xl font-bold text-white">{i + 1}</span>
              <div><p className="text-lg font-bold text-slate-900">{t(a)}</p><p className="text-slate-600">{t(b)}</p></div>
            </li>
          ))}
        </ol>
        </div>
        <Photo k="hero" className="h-52 w-full rounded-[2rem] sm:h-72 md:h-[460px]" />
      </section>
      <section className="page">
        <div className="grid overflow-hidden rounded-[2rem] bg-[color:var(--navy)] text-white md:grid-cols-2">
          <Photo k="cashier" className="h-56 w-full md:h-full" />
          <div className="p-8 sm:p-10">
            <h2 className="text-2xl font-bold sm:text-3xl">{t("cand_cta_title")}</h2>
            <p className="mt-2 text-white/80">{t("cand_cta_sub")}</p>
            <Link to={user?.role === "candidate" ? "/jobs" : "/start"} className="btn-orange mt-6" data-testid="home-cand-cta-btn"><UserPlus size={22} />{t("create_profile")}</Link>
          </div>
        </div>
      </section>
      {user?.role !== "candidate" && (
        <section className="page mt-12">
          <div className="card-soft flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
            <div className="flex items-center gap-4">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[color:var(--orange-soft)] text-[color:var(--orange)]"><Store size={28} /></span>
              <div><h2 className="text-xl font-bold text-[color:var(--navy)]">{t("shop_cta_title")}</h2><p className="text-slate-600">{t("shop_cta_sub")}</p></div>
            </div>
            <Link to="/employers" className="btn-ghost shrink-0" data-testid="home-employer-cta-btn">{t("shop_cta_btn")}<ArrowRight size={20} /></Link>
          </div>
        </section>
      )}
    </div>
  );
}
